/**
 * server-monitor-user-crawl-adapter — 疑似爬取真 DTO（`@novel-admin/shared` server-monitor-user-crawl）→「疑似爬取用户」卡的页面行。
 *
 * 时间沿用服务器监控页口径（fmtMonitorTime：今天 HH:mm:ss / 昨天 / MM-DD）；补充信息查不到落「—」，不编造。
 * 追查命令与飞书消息里的同形：该用户在最近一次命中窗口内的正文请求按 path 计数。计数是 Telemetry 估算值，采样时标「≈」；
 * 因被拒达阈值并入的用户正文请求数只是下界，标「≥」。
 */

import type {
  ServerMonitorService,
  ServerMonitorUserCrawl,
  ServerMonitorUserCrawlHit,
} from '@novel-admin/shared';
import { fmtMonitorTime } from '~/data/adapters/server-monitor-adapter';

export interface UserCrawlRow {
  key: string;
  userId: string;
  contentRequests: string;
  rejected: string;
  chapters: string;
  novels: string;
  /** 最近一次命中窗口 */
  window: string;
  firstSeen: string;
  status: { label: string; tone: 'warn' | 'neg' | 'default' };
  /** 行 hover：峰值 / 推送次数 / 采样提示 */
  title: string;
  command: string;
}

export interface UserCrawlView {
  tone: 'pos' | 'warn' | 'neg' | 'default';
  label: string;
  detail: string;
  ruleLabel: string;
  warnings: string[];
  rows: UserCrawlRow[];
  emptyNote: string | null;
}

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

/** 正文接口的 Telemetry trigger（与服务端疑似爬取查询同一口径）*/
const CONTENT_TRIGGER = 'GET /api/v1/chapters/REDACTED/content';

/** 日志脚本默认只取 100 条，--limit 按请求数留余量，避免按 path 计数被截断 */
export function buildUserCrawlCommand(
  service: ServerMonitorService,
  hit: ServerMonitorUserCrawlHit,
): string {
  const limit = Math.max(200, Math.ceil(hit.content_requests * 1.5));
  return `pnpm --filter @novel-admin/server logs --service ${service} --event request.complete --where ${shellQuote(`userId=${hit.user_id}`)} --where ${shellQuote(`$metadata.trigger=${CONTENT_TRIGGER}`)} --from ${hit.window_from} --to ${hit.window_to} --limit ${limit} --count-by path`;
}

function toRow(
  hit: ServerMonitorUserCrawlHit,
  service: ServerMonitorService,
  nowMs: number,
): UserCrawlRow {
  const approx = hit.sampled ? '≈ ' : '';
  return {
    key: hit.user_id,
    userId: hit.user_id,
    contentRequests: `${hit.content_requests_lower_bound ? '≥ ' : approx}${hit.content_requests}`,
    rejected: `${approx}${hit.rejected}`,
    chapters:
      hit.distinct_chapters === null
        ? '—'
        : `${hit.distinct_chapters}${hit.distinct_chapters_truncated ? '+' : ''}`,
    novels: hit.novels === null ? '—' : String(hit.novels),
    window: `${fmtMonitorTime(hit.window_from, nowMs)} – ${fmtMonitorTime(hit.window_to, nowMs)}`,
    firstSeen: fmtMonitorTime(hit.first_seen_at, nowMs),
    status: hit.pending
      ? { label: '待推送', tone: 'warn' }
      : hit.last_notified_at
        ? { label: '已推送', tone: 'default' }
        : { label: '未推送', tone: 'neg' },
    title: [
      `峰值 ${hit.peak_content_requests} 次 / 窗口`,
      `已推送 ${hit.notification_count} 次${hit.last_notified_at ? `（最近 ${fmtMonitorTime(hit.last_notified_at, nowMs)}）` : ''}`,
      hit.sampled ? '计数来自 Cloudflare 采样，为估算' : null,
    ]
      .filter(Boolean)
      .join(' · '),
    command: buildUserCrawlCommand(service, hit),
  };
}

export function toUserCrawlView(data: ServerMonitorUserCrawl, nowMs: number): UserCrawlView {
  const t = data.thresholds;
  const ruleLabel = `每用户近 ${t.window_minutes} 分钟拉章节正文 ≥ ${t.min_content_requests} 次 或 被拒（HTTP 422：限流 / 锁章等）≥ ${t.min_rejected} 次 → P1 · 同一用户 ${t.cooldown_minutes} 分钟内只推一次 · 列近 24h 命中`;
  const rows = data.hits.map((hit) => toRow(hit, data.service, nowMs));
  const tick = data.last_tick;
  const failing = data.failing_since !== null;
  const meta = !data.is_monitor_target
    ? { tone: 'default' as const, label: '非本环境' }
    : !data.configured
      ? { tone: 'warn' as const, label: '未配置' }
      : failing
        ? { tone: 'neg' as const, label: '查询失败' }
        : !tick
          ? { tone: 'default' as const, label: '未运行' }
          : rows.some((r) => r.status.label !== '已推送') || tick.breaches > 0
            ? { tone: 'warn' as const, label: '有命中' }
            : data.degraded_since !== null
              ? { tone: 'warn' as const, label: '口径存疑' }
              : { tone: 'pos' as const, label: '正常' };
  const detail = tick
    ? `最近一轮 ${fmtMonitorTime(tick.at, nowMs)} · 正文请求 ${tick.total_content_requests ?? '—'} · 候选 ${tick.candidates} · 命中 ${tick.breaches}`
    : '—';
  const warnings = [
    failing
      ? `自 ${fmtMonitorTime(data.failing_since, nowMs)} 起查询失败，恢复前看不到新的爬取行为`
      : null,
    // 口径降级：查询成功、照常判定，只是计数口径可能失效（不说成「查询失败」，也不盖掉「有命中」）
    data.degraded_since !== null
      ? `自 ${fmtMonitorTime(data.degraded_since, nowMs)} 起形状校验不过：${tick?.warning ?? '计数口径可能失效'}；告警照常判定，计数可能不准`
      : null,
    tick?.ok && tick.truncated
      ? '最近一轮达门槛用户超过查询上限，只评估了请求最多的部分用户'
      : null,
    tick?.ok && tick.sampled
      ? '最近一轮日志被 Cloudflare 采样：计数为估算，阈值附近可能误判'
      : null,
  ].filter((w): w is string => w !== null);
  return {
    tone: meta.tone,
    label: meta.label,
    detail,
    ruleLabel,
    warnings,
    rows,
    emptyNote:
      rows.length > 0
        ? null
        : !data.is_monitor_target
          ? '命中记录在该环境后台查看（本环境 cron 只评估同环境的 C 端服务）。'
          : !data.configured
            ? 'admin Worker 缺少 Telemetry token / account 或飞书配置，疑似爬取不评估。'
            : !tick
              ? 'cron 尚未写入结果（每 2 分钟一轮）。'
              : '近 24h 没有用户达到阈值。',
  };
}
